import Avo from '@susu/custom-made/Avo';
import CardList, { CardListType } from '@susu/custom-made/components/cardList/cardList';
import { useEventCallback } from '@susu/custom-made/hooks/useEventCallback';
import useScrollUp from '@susu/custom-made/hooks/useScrollUp';
import { ConfiguratorOriginsType, FabricViewMode } from '@susu/custom-made/utils/constants';
import { Clients, ConfiguratorOrigin, sortList } from '@susu/custom-made/utils/constants';
import type {
  BusinessRules,
  DefaultValues,
  Fabric,
  FabricFilter,
  OptionConfigurationType,
  typeFabricList,
  typefilterAttributeName,
  typeOptionValue,
} from '@susu/custom-made/utils/interfaces';
import { FABRIC_PANEL_TEST_IDS } from '@susu/custom-made/utils/testIds';
import { calculateFabricPrice, filterLowStockFabricList, onEnter } from '@susu/custom-made/utils/utils';
import type { RefObject } from 'react';
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { typeCard } from '../card/card';
import { OnBoardingTips } from '../onBoarding/onBoardingTips/onBoardingTips';
import Panel, { PanelPosition, PanelSize } from '../panel/panel';
import { PanelSearchHeader } from '../panelSearchHeader/panelSearchHeader';
import './fabricPanel.css';
import { useIsFirstRender } from '../sectionContainer/useFirstRender';
import { useApplyFabricBusinessRules } from './useApplyFabricBusinessRules';
import useFabricContext from '@susu/custom-made/providers/hooks/useFabricContext';
import useNavigationContext from '@susu/custom-made/providers/hooks/useNavigationContext';
import useUserContext from '@susu/custom-made/providers/hooks/useUserContext';
import Loader from '../loader/loader';
import { useFeatureIsOn } from '@susu/feature-flags/react';
import { useAvo } from '@susu/custom-made/hooks/useAvo';
import { FEATURE_TOGGLES } from '@susu/custom-made/feature-flags/featureToggles';
import { useI18N } from '@susu/i18n/client';
import { formatPrice } from '@susu/custom-made/utils/formatPrice';

const { SHOW_ONBOARDING_TIPS, ENABLE_SCROLL_TO_SELECTED, ENABLE_AVO_TRACKING } = FEATURE_TOGGLES;

const FilterList = lazy(() => import('../filterList/filterList'));
const SearchList = lazy(() => import('../searchList/searchList'));
const FabricInfo = lazy(() => import('../fabricInfo/fabricInfo'));
const FabricZoomOverlay = lazy(() => import('../fabricInfo/fabricZoomOverlay'));

export enum SidePanel {
  Filter = 'filter',
  FabricDetails = 'fabricDetails',
}

const FabricPanelComponent = ({
  fabricList,
  fabricSequence,
  fabricFilters,
  fabricPreFilters,
  fabricViewMode,
  navigation,
  isCustomizeFlow,
  origin,
  firstRenderRef,
  businessRules,
  options,
  optionValues,
  defaultValues,
  isInPreloadFlow,
}: {
  fabricList: typeFabricList;
  fabricSequence: number[];
  fabricFilters: FabricFilter[];
  fabricPreFilters: Record<string, string[]> | null;
  fabricViewMode: FabricViewMode;
  navigation: React.ReactNode;
  isCustomizeFlow: boolean;
  origin: ConfiguratorOriginsType;
  firstRenderRef: RefObject<boolean>;
  businessRules: BusinessRules;
  options: OptionConfigurationType;
  optionValues: typeOptionValue;
  defaultValues: DefaultValues;
  isInPreloadFlow: boolean;
}) => {
  const { t } = useI18N();
  const defaultFabricKeys = filterLowStockFabricList(fabricSequence.map(String), fabricList);
  const [isSearchShown, setIsSearchShown] = useState<boolean>(false);
  const [isFilterShown, setIsFilterShown] = useState<boolean>(false);
  const [isFabricInfoShown, setIsFabricInfoShown] = useState<boolean>(false);
  const [isFabricImageZoomed, setIsFabricImageZoomed] = useState<boolean>(false);
  const [selectedSortBy, setSelectedSortBy] = useState<string>(sortList.Default);
  const [filteredFabricListIds, setFilteredFabricListIds] = useState<string[]>(defaultFabricKeys);
  const [selectedFilter, setSelectedFilter] = useState<Record<string, string[]>>({});
  const scrollUp = useScrollUp();
  const [scanTrigger, setScanTrigger] = useState<boolean>(false);
  const [clickedSidePanel, setClickedSidePanel] = useState<string | null>(null);
  const { selectedFabric, setSelectedFabric } = useFabricContext();
  const { section } = useNavigationContext();
  const { client, product } = useUserContext();
  const isFirstRender = useIsFirstRender(firstRenderRef);
  const applyFabricBusinessRules = useApplyFabricBusinessRules({
    businessRules,
    options,
    optionValues,
    defaultValues,
    product,
    isInPreloadFlow,
  });
  const { getAvoStandardEventParams } = useAvo();
  const showOnboardingTipsFeatureEnabled = useFeatureIsOn(SHOW_ONBOARDING_TIPS);
  const scrollToSelectedFeatureEnabled = useFeatureIsOn(ENABLE_SCROLL_TO_SELECTED);
  const isAvoTrackingEnabled = useFeatureIsOn(ENABLE_AVO_TRACKING);
  const cardListContainerRef = useRef<HTMLDivElement>(null);
  const isManualSelectionDoneRef = useRef<boolean>(false);

  const shouldShowScanner = useMemo(() => {
    return client === Clients.inStore;
  }, [client]);

  const openFabricPanel = () => {
    setIsFilterShown(!isFilterShown);
    setClickedSidePanel(SidePanel.Filter);
    if (isAvoTrackingEnabled) {
      Avo.fabricFilterOpened({ ...getAvoStandardEventParams('fabric') });
    }
  };

  const scanClick = () => {
    openSearchPanel();
    setScanTrigger(true);
  };

  const openSearchPanel = () => {
    setIsSearchShown(true);
    setIsFilterShown(false);
    if (isAvoTrackingEnabled) {
      Avo.searchClicked({
        ...getAvoStandardEventParams('fabric'),
      });
    }
  };

  const getFilteredFabrics = useCallback(
    (selectedFabricIds: string[], selectedFilters: Record<string, string[]>) => {
      let tmpFilteredFabrics = [...selectedFabricIds];
      if (Object.keys(selectedFilters).length > 0) {
        for (const filterName in selectedFilters) {
          if (selectedFilters[filterName].length > 0) {
            tmpFilteredFabrics = tmpFilteredFabrics.filter(
              (id: string) =>
                selectedFilters[filterName].indexOf(fabricList[id][filterName as typefilterAttributeName]) >= 0,
            );
          }
        }
      }
      return tmpFilteredFabrics;
    },
    [fabricList],
  );

  const getSortedFabrics = useCallback(
    (selectedFabricIds: string[], selectedSortBy: string) => {
      const tmpFilteredFabrics = [...selectedFabricIds];
      if (selectedSortBy === sortList.Newest) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return fabricList[b].id - fabricList[a].id;
        });
      } else if (selectedSortBy === sortList.PriceHighLow) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return (
            calculateFabricPrice(product!, fabricList[b].prices.configurationTypes) -
            calculateFabricPrice(product!, fabricList[a].prices.configurationTypes)
          );
        });
      } else if (selectedSortBy === sortList.PriceLowHigh) {
        tmpFilteredFabrics.sort((a: string, b: string) => {
          return (
            calculateFabricPrice(product!, fabricList[a].prices.configurationTypes) -
            calculateFabricPrice(product!, fabricList[b].prices.configurationTypes)
          );
        });
      }
      return tmpFilteredFabrics;
    },
    [fabricList, product],
  );

  // When prefilters applies from the url
  useEffect(() => {
    if (fabricPreFilters && isFirstRender) {
      let fabricToSelect = fabricList[fabricSequence[0]];
      if (fabricSequence?.length > 0) {
        const allFabricListIds = filterLowStockFabricList(fabricSequence.map(String), fabricList);
        const filteredFabrics = getFilteredFabrics(allFabricListIds, fabricPreFilters);
        if (filteredFabrics.length > 0) {
          setSelectedFilter(fabricPreFilters);
          setFilteredFabricListIds(filteredFabrics);
          fabricToSelect = fabricList[filteredFabrics[0]];
        }
      }
      if (fabricToSelect) {
        applyFabricBusinessRules(fabricToSelect);
        setSelectedFabric(fabricToSelect);
      }
    }
  }, [selectedFabric, fabricList, fabricPreFilters, isFirstRender, getFilteredFabrics, applyFabricBusinessRules]);

  // Scroll to selected item on page load only (not on manual selection)
  useEffect(() => {
    if (
      selectedFabric &&
      cardListContainerRef.current &&
      scrollToSelectedFeatureEnabled &&
      !isManualSelectionDoneRef.current
    ) {
      // Use setTimeout to ensure the DOM is fully rendered
      const timeoutId = setTimeout(() => {
        const selectedCard = cardListContainerRef.current?.querySelector(`[data-testid="${selectedFabric.id}"]`);
        if (selectedCard) {
          selectedCard.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [selectedFabric, filteredFabricListIds, scrollToSelectedFeatureEnabled]);

  // The card click handlers keep a stable identity via useEventCallback so they don't
  // give all ~274 fabric cards a new prop (which would re-render the whole list) on
  // every selection, while still reading the latest values (selectedFabric, business
  // rules, view mode) at call time.
  const cardClick = useEventCallback((card: typeCard) => {
    applyFabricBusinessRules(card as Fabric, selectedFabric!);
    isManualSelectionDoneRef.current = true;
    setSelectedFabric(card as Fabric);

    if (isAvoTrackingEnabled && card.id !== selectedFabric?.id) {
      Avo.cmSelectionClicked({
        ...getAvoStandardEventParams('fabric'),
        cmItemType: Avo.CmItemType.FABRIC,
        currentBaseStyle: '',
        cmItemValue: `${card.name}`.toLowerCase(),
        cmSelectedView:
          fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
      });
    }
  });

  const countFilter = () => {
    const filterLowStockFabrics = filterLowStockFabricList(fabricSequence.map(String), fabricList);
    const filteredFabricIds = getFilteredFabrics(filterLowStockFabrics, selectedFilter);
    return filteredFabricIds.length;
  };

  const applyFilter = () => {
    const filterLowStockFabrics = filterLowStockFabricList(fabricSequence.map(String), fabricList);
    const filteredFabricIds = getFilteredFabrics(filterLowStockFabrics, selectedFilter);
    const sortedFabricsIds = getSortedFabrics(filteredFabricIds, selectedSortBy);
    setFilteredFabricListIds(sortedFabricsIds);
  };

  const calculateSelectedFilter = () => {
    return Object.keys(selectedFilter).reduce(
      (sum, key) => sum + parseFloat(selectedFilter[key].length.toString() || '0'),
      0,
    );
  };
  const cardInfoClick = useEventCallback(() => {
    if (isAvoTrackingEnabled) {
      Avo.fabricInformation({
        ...getAvoStandardEventParams('fabric'),
        cmItemValue: selectedFabric?.name.toLowerCase() ?? '',
        cmSelectedView:
          fabricViewMode === FabricViewMode.Layers ? Avo.CmSelectedView.PRODUCT_VIEW : Avo.CmSelectedView.MODEL_VIEW,
      });
    }
    setIsFabricInfoShown(true);
    setClickedSidePanel(SidePanel.FabricDetails);
  });

  const setTriggerScan = (status: boolean) => {
    setScanTrigger(status);
  };

  const closeSearchPanel = () => {
    setIsSearchShown(false);
  };

  const closeFabricInfo = useCallback(() => {
    setIsFabricInfoShown(false);
    setIsFabricImageZoomed(false);
  }, []);

  const handleFilterListClose = useCallback(() => {
    setIsFilterShown(false);
    if (isAvoTrackingEnabled) {
      Avo.fabricFilterClosed({ ...getAvoStandardEventParams('fabric') });
    }
  }, [isAvoTrackingEnabled, getAvoStandardEventParams]);

  return (
    <>
      {!isCustomizeFlow &&
        product === 'Suit' &&
        client === 'onLine' &&
        origin === ConfiguratorOrigin.Normal &&
        showOnboardingTipsFeatureEnabled && <OnBoardingTips />}

      <input type="hidden" id="shopify" />
      <div className="z-11 visible h-[188px] [transition:all_0.4s_cubic-bezier(0.5,0.05,0.1,1)] transform-[translateY(0)] md:h-[244px] lg:h-full lg:w-full lg:transform-[translateX(0)]">
        <div className="lg:absolute lg:z-2 lg:top-0 lg:left-0 lg:right-0 lg:h-[82px] lg:bg-white lg:text-center 2xl:h-[88px]">
          {navigation}
        </div>
        <PanelSearchHeader
          isVisible={scrollUp.scrollingUp}
          itemCountLabel={t(filteredFabricListIds.length === 1 ? 'itemCount.fabric' : 'itemCount.fabrics', { count: String(filteredFabricListIds.length) })}
          className="lg:pt-0 lg:px-20 lg:gap-12"
          searchSlot={
            <div
              className="absolute bottom-8 left-4 text-xl z-1 cursor-pointer flex h-44 w-44 justify-center items-center md:p-20 md:bottom-22 md:left-20 md:text-2xl lg:relative lg:inset-auto lg:after:content-[''] lg:after:absolute lg:after:-inset-[6px] lg:after:rounded-full lg:p-0 lg:w-20 lg:h-20 lg:justify-self-start lg:text-sm lg:leading-sm lg:rounded-full lg:gap-4 lg:[&>i]:text-xl lg:[&>i]:leading-sm lg:[&>i]:pt-0"
              data-testid={FABRIC_PANEL_TEST_IDS.SEARCH_BTN}
              onClick={openSearchPanel}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                onEnter(e, openSearchPanel);
              }}
            >
              <i className="susu-icons" translate="no">
                search
              </i>
            </div>
          }
          filterSlot={
            <div
              className="absolute bottom-8 right-4 text-xl z-1 cursor-pointer flex h-44 w-44 justify-center items-center md:p-20 md:bottom-[21px] md:right-20 lg:justify-self-end lg:relative lg:inset-auto lg:after:content-[''] lg:after:absolute lg:after:-inset-[6px] lg:after:rounded-full lg:p-0 lg:w-20 lg:h-20 lg:text-sm lg:leading-sm lg:rounded-full lg:gap-4"
              data-testid={FABRIC_PANEL_TEST_IDS.FILTER_BTN}
              onClick={openFabricPanel}
              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                onEnter(e, openFabricPanel);
              }}
            >
              {calculateSelectedFilter() > 0 && (
                <span className="w-14 h-14 rounded-full border border-white bg-gray-800 absolute right-8 bottom-22 pt-[2.75px] px-1 pb-[3.25px] text-[8px] text-white font-medium flex items-center justify-center md:-right-4 md:bottom-14 lg:-right-6 lg:top-8 lg:w-16 lg:h-16 lg:text-[9px]">
                  {calculateSelectedFilter()}
                </span>
              )}
              <span className="cm-fabric-filter-icon shrink-0 bg-center bg-no-repeat bg-contain w-20 h-20 md:w-24 md:h-24 lg:w-20 lg:h-20"></span>
            </div>
          }
        />
        <div
          className="flex justify-center lg:h-[calc(100dvh-var(--webstore-header-height))] lg:overflow-auto lg:w-full lg:pt-[122px] 2xl:pt-[128px]"
          ref={cardListContainerRef}
          onScroll={e => {
            scrollUp.handleScroll(e, 43);
          }}
        >
          <CardList
            cardItems={fabricList}
            cardSubItems={filteredFabricListIds}
            selectedItemId={selectedFabric ? selectedFabric.id : null}
            cardClick={cardClick}
            infoIconClick={cardInfoClick}
            isGroup={false}
            cardListType={CardListType.Fabric}
            testId={FABRIC_PANEL_TEST_IDS.SUBGROUP_LIST}
            section={section}
            scanClick={shouldShowScanner ? scanClick : undefined}
            isGrid
          />
        </div>
        <div className="absolute bottom-10 w-[calc(100%-90px)] text-center mx-[45px] md:bottom-[13px] lg:hidden">
          <div
            className="text-sm leading-sm font-medium overflow-hidden text-ellipsis text-nowrap"
            data-testid={FABRIC_PANEL_TEST_IDS.CARD_TEXT_MOBILE}
          >
            {selectedFabric?.name}
          </div>
          <div
            className="text-xs font-light md:text-[13px] md:leading-[19px]"
            data-testid={FABRIC_PANEL_TEST_IDS.CARD_SUBTEXT_MOBILE}
          >
            {selectedFabric &&
              product &&
              formatPrice(
                calculateFabricPrice(product, selectedFabric.prices?.configurationTypes),
                selectedFabric.prices?.code ?? '',
              )}
          </div>
        </div>
      </div>

      {/* Search Panel */}
      <Panel
        isActive={isSearchShown}
        closeClick={closeSearchPanel}
        panelSize={PanelSize.Multi}
        testIdSubfix="fabric-search"
      >
        {isSearchShown && (
          <Suspense fallback={<Loader />}>
            <SearchList
              onClose={closeSearchPanel}
              itemList={fabricList}
              filteredItemListIds={filteredFabricListIds}
              selectedItemId={selectedFabric ? selectedFabric.id : null}
              infoIconClick={cardInfoClick}
              cardClick={cardClick}
              cardListType={CardListType.Fabric}
              inputTestId="fabric-search-input"
              showScanner={shouldShowScanner}
              triggerScanner={scanTrigger}
              setTriggerScan={setTriggerScan}
            />
          </Suspense>
        )}
      </Panel>

      {/* FabricDetails Panel */}
      <Panel
        isActive={isFabricInfoShown}
        closeClick={closeFabricInfo}
        panelSize={PanelSize.Content}
        panelPosition={PanelPosition.Side}
        testIdSubfix="fabric-details"
        zIndex={clickedSidePanel === SidePanel.FabricDetails ? 13 : 12}
      >
        {isFabricInfoShown && selectedFabric && product && (
          <Suspense fallback={<Loader />}>
            <FabricInfo
              showBookletInfo={client === Clients.inStore}
              closeFabricInfo={closeFabricInfo}
              openImageZoom={() => {
                setIsFabricImageZoomed(true);
              }}
            />
          </Suspense>
        )}
      </Panel>

      {/* A sibling of the Panel on purpose: the panel is translated on lg, which makes
          it the containing block for fixed descendants and clips them to its box. */}
      {isFabricImageZoomed && isFabricInfoShown && selectedFabric && (
        <Suspense fallback={null}>
          <FabricZoomOverlay
            onClose={() => {
              setIsFabricImageZoomed(false);
            }}
          />
        </Suspense>
      )}

      {/* Filter Panel */}
      <Panel
        isActive={isFilterShown}
        closeClick={() => {
          setIsFilterShown(false);
        }}
        panelSize={PanelSize.Full}
        testIdSubfix="fabric-filter"
        panelPosition={PanelPosition.Side}
        zIndex={clickedSidePanel === SidePanel.Filter ? 13 : 12}
      >
        {isFilterShown && (
          <Suspense fallback={<Loader />}>
            <FilterList
              filters={fabricFilters}
              onClose={handleFilterListClose}
              onFilterChange={countFilter}
              onFilterApply={applyFilter}
              selectedSortBy={selectedSortBy}
              selectedFilter={selectedFilter}
              setSelectedSortBy={setSelectedSortBy}
              setSelectedFilter={setSelectedFilter}
              fabricList={fabricList}
              fabricSequence={fabricSequence}
            />
          </Suspense>
        )}
      </Panel>
    </>
  );
};

FabricPanelComponent.displayName = 'FabricPanel';

export const FabricPanel = memo(FabricPanelComponent);
